import { PaymentMethod } from "../../../../common/enums";
import { isUSA, redactAccountNumber } from "../../../../common/utils";
import { type DetailedEftPaymentPrefObjectType } from "../../common/types";
import { type SelectedBankCountry } from "../../Context/types";
import { getIdFromCountry, getNameFromCountry, mapCountryToValueSafely } from "../../mappers";
import { AccountDetailsInputFields } from "./AccountDetails/constants";
import { BankDetailsInputFields } from "./BankDetails/constants";
import { type EftPaymentPrefInputType } from "./constants";

export const mapFormStateToContextState = (
  values: EftPaymentPrefInputType,
  bankCountry?: SelectedBankCountry,
): DetailedEftPaymentPrefObjectType => {
  const accountHolderCountryValue = values[AccountDetailsInputFields.AddressCountry];
  const hasValidCountryFormat =
    !!accountHolderCountryValue && accountHolderCountryValue.includes(":");
  const accountHolderCountryName = hasValidCountryFormat
    ? getNameFromCountry(accountHolderCountryValue)
    : "";
  const accountHolderCountryId = hasValidCountryFormat
    ? getIdFromCountry(accountHolderCountryValue)
    : "";
  const isUsAccountHolderAddress = isUSA(accountHolderCountryName);

  return {
    __typename: "EftPaymentPrefObjectType",
    id: null,
    paymentPrefId: null,
    paymentMethod: PaymentMethod.EFT,
    bank: {
      __typename: "EftBankObjectType",
      name: values[BankDetailsInputFields.BankName]?.trim() || null,
      institutionNumber: values[BankDetailsInputFields.InstitutionNumber]?.trim() || null,
      transitNumber: values[BankDetailsInputFields.TransitNumber]?.trim() || null,
      address: {
        __typename: "PaymentPrefAddressObjectType",
        street: null,
        street2: null,
        street3: null,
        city: null,
        region: null,
        state: null,
        postalCode: null,
        // Bank country is resolved upstream — either from SelectPaymentMethodV2 selection (context)
        // or via the GetCountryCodes fallback by alpha2 "CA" — and passed in as `bankCountry`.
        country: bankCountry
          ? {
              __typename: "CountryObjectType",
              id: bankCountry.id,
              name: bankCountry.name,
              alpha2code: bankCountry.alpha2code ?? null,
              code: null,
            }
          : null,
      },
    },
    accountName: values[AccountDetailsInputFields.AccountName]?.trim() || null,
    accountNumber: values[AccountDetailsInputFields.AccountNumber]?.trim() || null,
    additionalInstructions:
      values[AccountDetailsInputFields.AdditionalInstructions]?.trim() || null,
    accountHolderAddress: {
      __typename: "PaymentPrefAddressObjectType",
      street: values[AccountDetailsInputFields.AddressStreetLine1]?.trim() || null,
      street2: values[AccountDetailsInputFields.AddressStreetLine2]?.trim() || null,
      street3: null,
      city: values[AccountDetailsInputFields.AddressCity]?.trim() || null,
      region: !isUsAccountHolderAddress
        ? values[AccountDetailsInputFields.AddressProvince]?.trim() || null
        : null,
      state: isUsAccountHolderAddress
        ? {
            __typename: "StateObjectType",
            code: values[AccountDetailsInputFields.AddressState]?.trim() ?? "",
            id: values[AccountDetailsInputFields.AddressState]?.trim() ?? "",
            name: "",
          }
        : null,
      postalCode: values[AccountDetailsInputFields.AddressZip]?.trim() || null,
      country: accountHolderCountryId
        ? {
            __typename: "CountryObjectType",
            id: accountHolderCountryId,
            name: accountHolderCountryName,
            alpha2code: null,
            code: null,
          }
        : null,
    },
    bankInfoRedacted: `${
      values[BankDetailsInputFields.BankName] ?? "Bank"
    } account ending in -${
      values[AccountDetailsInputFields.AccountNumber]
        ? redactAccountNumber(values[AccountDetailsInputFields.AccountNumber])
        : ""
    }`,
  };
};

export const mapContextStateToFormState = (
  input?: DetailedEftPaymentPrefObjectType,
): EftPaymentPrefInputType => {
  const accountHolderCountryName = input?.accountHolderAddress?.country?.name ?? "";
  const isUsAccountHolderAddress = isUSA(accountHolderCountryName);
  return {
    [BankDetailsInputFields.BankName]: input?.bank?.name ?? "",
    [BankDetailsInputFields.BankCountry]: "Canada",
    [BankDetailsInputFields.BankCountryValidatedCountry]: "",
    [BankDetailsInputFields.InstitutionNumber]: input?.bank?.institutionNumber ?? "",
    [BankDetailsInputFields.TransitNumber]: input?.bank?.transitNumber ?? "",
    [AccountDetailsInputFields.AccountName]: input?.accountName ?? "",
    [AccountDetailsInputFields.AccountNumber]: input?.accountNumber ?? "",
    [AccountDetailsInputFields.ConfirmAccountNumber]: input?.accountNumber ?? "",
    [AccountDetailsInputFields.AdditionalInstructions]: input?.additionalInstructions ?? "",
    [AccountDetailsInputFields.AddressStreetLine1]: input?.accountHolderAddress?.street ?? "",
    [AccountDetailsInputFields.AddressStreetLine2]: input?.accountHolderAddress?.street2 ?? "",
    [AccountDetailsInputFields.AddressCity]: input?.accountHolderAddress?.city ?? "",
    [AccountDetailsInputFields.AddressState]: isUsAccountHolderAddress
      ? input?.accountHolderAddress?.state?.code ?? ""
      : "",
    [AccountDetailsInputFields.AddressProvince]: !isUsAccountHolderAddress
      ? input?.accountHolderAddress?.region ?? ""
      : "",
    [AccountDetailsInputFields.AddressZip]: input?.accountHolderAddress?.postalCode ?? "",
    [AccountDetailsInputFields.AddressCountry]: mapCountryToValueSafely(
      input?.accountHolderAddress?.country?.id ?? undefined,
      input?.accountHolderAddress?.country?.name ?? undefined,
    ),
  };
};
